import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import * as api from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useScrollToTop } from '@/hooks/use-scroll-to-top'
import {
  RESERVATION_TASK_PAGE_SIZE,
  ReservationTask,
  ReservationTaskChange,
  ReservationTaskFilter,
  ReservationTaskQuery,
  ReservationTaskSort,
} from '@/models/reservation/reservation-task'
import {
  INITIAL_RESERVATION_TASK_QUERY,
  parseReservationTaskQuery,
  stringifyReservationTaskQuery,
  toReservationTaskListParams,
} from '@/utils/reservation-task'

type Params = {
  /** 未対応件数に影響する操作（再読み込み・更新）の後に呼ぶ。件数は宿泊管理画面（list.tsx）が持つ */
  onTasksChanged: () => void
}

export const useReservationTaskList = ({ onTasksChanged }: Params) => {
  const history = useHistory()
  const { errorHandler } = useErrorHandler()
  const { scrollRef, scrollToTop } = useScrollToTop()
  const [query, setQuery] = useState<ReservationTaskQuery>(
    () => parseReservationTaskQuery(window.location.search) ?? INITIAL_RESERVATION_TASK_QUERY,
  )
  const [tasks, setTasks] = useState<ReservationTask[]>([])
  const [total, setTotal] = useState(0)
  // マウント直後に必ず取得するため true から始める。false だと取得前の一瞬「データがありません」が出る
  const [isLoading, setIsLoading] = useState(true)
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([])
  const isUpdatingRef = useRef(false)
  const isMountedRef = useRef(true)
  // 古い応答が新しい応答の結果を上書きしないよう、最後に始めた取得の番号を持つ
  const latestRequestIdRef = useRef(0)

  const fetchTasks = async (nextQuery: ReservationTaskQuery, options: { scrollToTop?: boolean } = {}): Promise<void> => {
    const requestId = latestRequestIdRef.current + 1
    latestRequestIdRef.current = requestId
    setIsLoading(true)
    try {
      const result = await api.fetchReservationTasks(toReservationTaskListParams(nextQuery))
      if (!isMountedRef.current || requestId !== latestRequestIdRef.current) {
        return
      }
      const lastPage = Math.max(1, Math.ceil(result.total / RESERVATION_TASK_PAGE_SIZE))
      if (result.tasks.length === 0 && nextQuery.page > lastPage) {
        // 更新で最後のページのタスクが条件から外れた・URL を書き換えたなどで、ページが範囲外になったときは最後のページを取り直す
        await fetchTasks({ ...nextQuery, page: lastPage }, options)
        return
      }
      // 条件と URL は取得に成功してから反映する。失敗したときに、表示中の一覧と条件・URL が食い違わないようにするため
      setQuery(nextQuery)
      history.replace({ search: stringifyReservationTaskQuery(nextQuery) })
      setTasks(result.tasks)
      setTotal(result.total)
      // 取り直した一覧に無いタスクの選択は残さない（更新に失敗して選択を残したまま取り直す場合のため）
      setSelectedTaskIds(ids => ids.filter(id => result.tasks.some(task => task.id === id)))
      // 先頭へ戻すのは絞り込み・並び替え・ページ送りのときだけ。更新や再読み込みで戻すと、一覧の下の行を続けて直せなくなるため
      if (options.scrollToTop) {
        scrollToTop()
      }
    } catch (error) {
      if (isMountedRef.current) {
        errorHandler(error)
      }
    } finally {
      // 最後のページの取り直しは新しい取得として始まり、ローディングはそちらが解除する
      if (isMountedRef.current && requestId === latestRequestIdRef.current) {
        setIsLoading(false)
      }
    }
  }

  // タブを開いたときに1回だけ、URL から読んだ条件で取得する。以降は操作のたびに fetchTasks を呼ぶ。
  // アンマウント時は isMountedRef を下ろし、遅れて届いた応答で URL や状態を書き換えないようにする。
  // 戻る操作でタブを離れた直後に history.replace が走ると、表示中の別のタブの URL をタスク管理タブの URL で上書きし、引き戻してしまうため
  useEffect(() => {
    fetchTasks(query)
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const applyFilter = (filter: ReservationTaskFilter) => {
    setSelectedTaskIds([])
    fetchTasks({ ...query, filter, page: 1 }, { scrollToTop: true })
  }

  const changeSort = (sort: ReservationTaskSort) => {
    setSelectedTaskIds([])
    fetchTasks({ ...query, sort, page: 1 }, { scrollToTop: true })
  }

  const changePage = (page: number) => {
    setSelectedTaskIds([])
    fetchTasks({ ...query, page }, { scrollToTop: true })
  }

  const reload = () => {
    setSelectedTaskIds([])
    fetchTasks(query)
    onTasksChanged()
  }

  const toggleTask = (taskId: number, checked: boolean) => {
    setSelectedTaskIds(ids => {
      const others = ids.filter(id => id !== taskId)
      return checked ? [...others, taskId] : others
    })
  }

  const toggleAllTasks = (checked: boolean) => {
    setSelectedTaskIds(checked ? tasks.map(task => task.id) : [])
  }

  const updateTasks = async (taskIds: number[], change: ReservationTaskChange) => {
    // 保存中の二重送信を防ぐ。全画面のローディングが出るまでの間に続けて押された場合のため
    if (isUpdatingRef.current) {
      return
    }
    isUpdatingRef.current = true
    setIsLoading(true)
    try {
      try {
        await api.updateReservationTasks({ taskIds, ...change })
        setSelectedTaskIds([])
      } catch (error) {
        errorHandler(error)
      }
      // 成功しても失敗しても取り直し、画面の値をサーバーの値にそろえる（規約 D-1）
      await fetchTasks(query)
      onTasksChanged()
    } finally {
      isUpdatingRef.current = false
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }

  return {
    query,
    tasks,
    total,
    isLoading,
    selectedTaskIds,
    scrollRef,
    applyFilter,
    changeSort,
    changePage,
    reload,
    toggleTask,
    toggleAllTasks,
    updateTasks,
  }
}
